import { createApi } from '@reduxjs/toolkit/query/react'
import axiosBaseQuery from 'api/axiosBaseQuery'
import {
  Enroll2faCompleteRequest,
  Enroll2faResponse,
  Enroll2faStartRequest,
  IApiResponse,
  LoginRequestBody,
  LoginResponse,
  MeResponse,
  RegisterRequestBody,
  SignupOptionsResponse,
  UpdateMeBody
} from 'api/types'

// Every BE response is wrapped in IApiResponse<T>. RTK queries unwrap to T.
const unwrap = <T>(envelope: IApiResponse<T>): T => envelope.data

export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: axiosBaseQuery({ baseUrl: process.env.REACT_APP_BASE_URL ?? '' }),
  endpoints: (builder) => ({
    login: builder.mutation<LoginResponse, LoginRequestBody>({
      query: (credentials) => ({
        url: '/auth/login',
        method: 'POST',
        data: credentials,
        includeAuth: false
      }),
      transformResponse: (resp: IApiResponse<LoginResponse>) => unwrap(resp)
    }),

    // --- Forced TOTP enrolment (prod MFA enforcement) ---
    // Both are public: the enrolmentToken travels in the body, never in the
    // Authorization header (the API rejects purpose-scoped tokens there).
    startTwoFactorEnrollment: builder.mutation<
      Enroll2faResponse,
      Enroll2faStartRequest
    >({
      query: (body) => ({
        url: '/auth/2fa/enrollment/start',
        method: 'POST',
        data: body,
        includeAuth: false
      }),
      transformResponse: (resp: IApiResponse<Enroll2faResponse>) => unwrap(resp)
    }),

    completeTwoFactorEnrollment: builder.mutation<
      LoginResponse,
      Enroll2faCompleteRequest
    >({
      query: (body) => ({
        url: '/auth/2fa/enrollment/complete',
        method: 'POST',
        data: body,
        includeAuth: false
      }),
      transformResponse: (resp: IApiResponse<LoginResponse>) => unwrap(resp)
    }),

    register: builder.mutation<LoginResponse, RegisterRequestBody>({
      query: (body) => ({
        url: '/public/auth/register',
        method: 'POST',
        data: body,
        includeAuth: false
      }),
      transformResponse: (resp: IApiResponse<LoginResponse>) => unwrap(resp)
    }),

    getSignupOptions: builder.query<SignupOptionsResponse, void>({
      query: () => ({
        url: '/public/auth/signup-options',
        method: 'GET',
        includeAuth: false
      }),
      transformResponse: (resp: IApiResponse<SignupOptionsResponse>) =>
        unwrap(resp)
    }),

    logout: builder.mutation<void, { refreshToken: string }>({
      query: (body) => ({
        url: '/auth/logout',
        method: 'POST',
        data: body
      })
    }),

    me: builder.query<MeResponse, void>({
      query: () => ({ url: '/auth/me', method: 'GET' }),
      transformResponse: (resp: IApiResponse<MeResponse>) => unwrap(resp)
    }),

    updateMe: builder.mutation<MeResponse, UpdateMeBody>({
      query: (body) => ({ url: '/auth/me', method: 'PATCH', data: body }),
      transformResponse: (resp: IApiResponse<MeResponse>) => unwrap(resp)
    }),

    forgotPassword: builder.mutation<{ message: string }, { email: string }>({
      query: (body) => ({
        url: '/auth/forgot-password',
        method: 'POST',
        data: body,
        includeAuth: false
      }),
      transformResponse: (resp: IApiResponse<{ message: string }>) =>
        unwrap(resp)
    }),

    resetPassword: builder.mutation<
      { message: string },
      { token: string, newPassword: string }
    >({
      query: (body) => ({
        url: '/auth/reset-password',
        method: 'POST',
        data: body,
        includeAuth: false
      }),
      transformResponse: (resp: IApiResponse<{ message: string }>) =>
        unwrap(resp)
    }),

    activateAccount: builder.mutation<{ message: string }, { token: string }>({
      query: (body) => ({
        url: '/auth/activate-account',
        method: 'POST',
        data: body,
        includeAuth: false
      }),
      transformResponse: (resp: IApiResponse<{ message: string }>) =>
        unwrap(resp)
    }),

    changePassword: builder.mutation<
      { message: string },
      { currentPassword: string, newPassword: string }
    >({
      query: (body) => ({
        url: '/auth/me/password',
        method: 'PATCH',
        data: body
      }),
      transformResponse: (resp: IApiResponse<{ message: string }>) =>
        unwrap(resp)
    })
  })
})

export const {
  useLoginMutation,
  useStartTwoFactorEnrollmentMutation,
  useCompleteTwoFactorEnrollmentMutation,
  useRegisterMutation,
  useLogoutMutation,
  useMeQuery,
  useLazyMeQuery,
  useUpdateMeMutation,
  useGetSignupOptionsQuery,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useActivateAccountMutation,
  useChangePasswordMutation
} = authApi

export default authApi
